import { useState, useContext, useMemo, ReactNode, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

import { useApi } from '../features/api/useApi';
import {
  CampusContext,
  CampusContextType,
  CampusFeatureFlags,
  CampusRecentEntities,
  ThermalZoneTactic,
  ControlMode,
  ThermalZoneSummary,
} from './CampusContext';
import { CONTROL_MODE } from '../config/control';
import { setThermalZoneStatus } from '../utilities/ProcessRealtimeZoneHelper';

import configuration from '../config.js';

const campusHasBattery = (batteryData: any): boolean => {
  if (!batteryData || batteryData.length === 0 || batteryData?.gridInterconnects?.length === 0) {
    return false;
  } else if (batteryData?.gridInterconnects?.length > 0) {
    return batteryData.gridInterconnects.some(
      (interconnect: any) => interconnect.acBatteries.length > 0
    );
  }
  return true;
};

const assignControlMode = (tactic: ThermalZoneTactic): ControlMode | null => {
  const now = new Date();
  const start = new Date(tactic.startDateTime);
  const end = new Date(tactic.endDateTime);

  // if the tactic blockCode is not a valid CONTROL_MODE, return null
  // There are some unexpected blockCodes for example 'NotSelected'
  if (!CONTROL_MODE[tactic.blockCode.toUpperCase()]) {
    return null;
  }

  if (now >= start && now <= end) {
    const untilDesc = () => {
      const untilString =
        end > now
          ? `Until ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
          : '';

      if (untilString.length > 0) {
        return { untilDesc: untilString };
      }
    };

    return {
      id: `${tactic.blockCode}-${tactic.startDateTime}`,
      mode: tactic.blockCode,
      isActive: true,
      duration: {
        startTime: tactic.startDateTime,
        endTime: tactic.endDateTime,
      },
      ...untilDesc(),
    };
  }

  return {
    id: `${tactic.blockCode}-${tactic.startDateTime}`,
    mode: tactic.blockCode,
    isActive: false,
    duration: {
      startTime: tactic.startDateTime,
      endTime: tactic.endDateTime,
    },
  };
};

interface CampusContextProviderProps {
  children: ReactNode;
}

export const CampusContextProvider = ({ children }: CampusContextProviderProps) => {
  const { fetchJson } = useApi();
  const location = useLocation();
  const pathParts = location.pathname.split('/');
  const page = pathParts[1];
  const campusId = pathParts[2] || '';
  const [controlModes, setControlModes] = useState<ControlMode[]>([]);
  const [buildingId, setBuildingId] = useState('');
  const [gxId, setGxId] = useState('');
  const [zoneId, setZoneId] = useState('');

  const {
    data: batteryData,
    // isLoading: batteryLoading,
    // error: batteryError,
  } = useQuery({
    queryKey: ['devices', campusId, 'battery'],
    queryFn: () => fetchJson(`${configuration['backend_host']}/ahiapi/${campusId}/acBatteries`),
    enabled: !!campusId,
  });

  const {
    data: campusComfortData,
    isLoading: campusComfortLoading,
    error: campusComfortError,
  } = useQuery({
    queryKey: ['campus', campusId, 'comfort'],
    queryFn: () => fetchJson(`${configuration['backend_host']}/ahiapi/${campusId}/campus/comfort`),
    enabled: !!campusId,
    refetchInterval: 5000,
  });

  const { data: campusEnergyData } = useQuery({
    queryKey: ['campus', campusId, 'energy'],
    queryFn: () => fetchJson(`${configuration['backend_host']}/ahiapi/${campusId}/campus/energy`),
    enabled: !!campusId,
  });

  const { data: campusEvData } = useQuery({
    queryKey: ['devices', campusId, 'ev'],
    queryFn: () =>
      fetchJson(`${configuration['backend_host']}/ahiapi/${campusId}/campus/evChargers`),
    enabled: !!campusId,
  });

  const { data: solarData } = useQuery({
    queryKey: ['campus', 'solar'],
    queryFn: () => {
      return fetchJson(`${configuration['backend_host']}/ahiapi/trendsSolarCampuses`);
    },
    // refetchInterval: 5000,
    retryDelay: 5000,
    enabled: !!campusId,
  });

  const campusInterconnects = useMemo(
    () =>
      campusEnergyData?.gridInterconnects?.map(
        (interconnect: any) => interconnect.gridInterconnectDescription.gridInterconnectEntityKey
      ) ?? [],
    [campusEnergyData]
  );

  const { data: interconnectData } = useQuery({
    queryKey: ['interconnect', campusInterconnects[0]],
    queryFn: () => {
      if (campusInterconnects.length === 0) return;
      return fetchJson(
        `${configuration['backend_host']}/admin/monitor/${campusInterconnects[0]}/gridInterconnect/graphData/live`
      );
    },
    // refetchInterval: 5000,
    retryDelay: 5000,
    enabled: campusInterconnects.length > 0 && !!campusId,
  });

  const solarGx = useMemo(
    () => solarData?.filter((campus: any) => campus.campusEntityKeyToken === campusId) ?? [],
    [solarData, campusId]
  );

  const campusEvChargers = useMemo(
    () => campusEvData?.gridInterconnectEvChargers ?? [],
    [campusEvData]
  );

  const hasSingleGx = campusInterconnects.length === 1;
  const hasSolar = solarGx.some((campus: any) => campus.energySite !== null);
  const hasEv = campusEvChargers.some((campusEvCharger: any) =>
    campusEvCharger.evChargerGroups?.some((group: any) => group.evChargers?.length > 0)
  );

  useEffect(() => {
    setBuildingId('');
    setGxId('');
    setZoneId('');
  }, [campusId]);

  useEffect(() => {
    if (page === 'comfort') {
      if (pathParts[3]) setBuildingId(pathParts[3]);
      if (pathParts[4]) setZoneId(pathParts[4]);
    } else if (page && ['energy', 'trends', 'solar'].includes(page)) {
      if (pathParts[3]) setGxId(pathParts[3]);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!interconnectData) return;
    getThermalZoneTactics();
  }, [interconnectData]);

  const campusName = campusComfortData?.campusDescription?.longCampusDisplayName || 'Campus';

  const getThermalZoneTactics: () => void = () => {
    const { hasThermalZones } = featureFlags;
    const thermalZoneTactics = interconnectData?.blockCategories?.find(
      (category: any) => category.categoryName === 'ThermalZoneTactic'
    )?.blocks;
    const newThermalZoneTactics: ControlMode[] = [];

    for (const tactic of thermalZoneTactics || []) {
      const controlMode = assignControlMode(tactic);
      if (controlMode) {
        newThermalZoneTactics.push(controlMode);
      }
    }

    setControlModes(hasThermalZones ? newThermalZoneTactics : []);
  };

  const getZoneActiveControlStrategy = (zoneData: any): ControlMode | null => {
    if (zoneData?.offline) return { id: 'offline', mode: 'OFFLINE', isActive: true };
    if (zoneData?.unmanaged) return { id: 'unmanaged', mode: 'UNMANAGED', isActive: true };
    if (zoneData?.inLocalOverride)
      return {
        id: `local-override-${zoneData.thermalZoneEntityKey}`,
        mode: 'LOCAL_OVERRIDE',
        isActive: true,
      };
    if (zoneData?.inOverride)
      return {
        id: `remote-override-${zoneData.thermalZoneEntityKey}`,
        mode: 'REMOTE_OVERRIDE',
        isActive: true,
      };

    if (zoneData?.inEvent && zoneData?.eventName)
      return {
        id: `event-${zoneData.thermalZoneEntityKey}`,
        mode: 'EVENT',
        isActive: true,
      };

    const activeTactic = controlModes.find((mode) => mode.isActive);
    if (!activeTactic && !zoneData?.unmanaged) {
      return { id: 'standard', mode: 'STANDARD', isActive: true };
    }

    return activeTactic ?? null;
  };

  const thermalZones: ThermalZoneSummary[] =
    campusComfortData?.buildings?.flatMap((building: any) =>
      (building.thermalZones ?? []).map(
        (zone: any): ThermalZoneSummary => ({
          thermalZoneEntityKey: zone.thermalZoneEntityKey,
          name: zone.longThermalZoneDisplayName,
          buildingEntityKey: building.buildingDescription.buildingEntityKey,
          buildingName: building.buildingDescription.longBuildingDisplayName,
          status: setThermalZoneStatus(zone),
          activeControlStrategy: getZoneActiveControlStrategy(zone),
        })
      )
    ) ?? [];

  const featureFlags: CampusFeatureFlags = {
    hasBattery: batteryData ? campusHasBattery(batteryData) : false,
    hasSolar: hasSolar,
    hasEv: hasEv,
    hasSingleGX: hasSingleGx,
    hasThermalZones: thermalZones.length > 0,
  };

  // TODO: Handle data at the page level
  // const campusData = {
  //   evData: campusEvData,
  //   batteryData: batteryData,
  //   thermalZoneData: thermalZones,
  //   solarData: solarGx,
  //   comfortData: {
  //     data: campusComfortData,
  //     isLoading: campusComfortLoading,
  //     error: campusComfortError,
  //   },
  // };

  const contextValue: CampusContextType = {
    meta: {
      campusName: campusName || '',
      campusId: campusId || '',
      timezone: interconnectData?.timeZoneCode || '',
    },
    featureFlags,
    controlModes: controlModes,
    thermalZones,
    campusData: {
      data: campusComfortData,
      isLoading: campusComfortLoading,
      error: campusComfortError,
    },
    recentEntities: {
      campusId,
      buildingId,
      gxId,
      zoneId,
    },
  };

  return <CampusContext.Provider value={contextValue}>{children}</CampusContext.Provider>;
};

export const useCampusContext = (): CampusContextType => {
  const context = useContext(CampusContext);
  if (context === undefined) {
    throw new Error('useCampusContext must be used within a CampusContextProvider');
  }
  return context;
};

// hooks
export const useCampusFeatureFlags = (): CampusFeatureFlags => useCampusContext().featureFlags;
export const useCampusMeta = (): CampusContextType['meta'] => useCampusContext().meta;
export const useCampusData = (): CampusContextType['campusData'] => useCampusContext().campusData;
export const useCampusControlModes = (): ControlMode[] | undefined =>
  useCampusContext().controlModes;
export const useCampusThermalZones = (): ThermalZoneSummary[] | undefined =>
  useCampusContext().thermalZones;
export const useCampusWeather = (): any => {
  const campusData = useCampusContext().campusData?.data?.weather;
  return campusData || null;
};
export const useRecentEntities = (): CampusRecentEntities => useCampusContext().recentEntities;
